import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Dumbbell, Home, Building2, Loader2, AlertCircle, Apple } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEVELS = [
  { value: 'principiante', label: 'Principiante' },
  { value: 'intermedio', label: 'Intermedio' },
  { value: 'avanzado', label: 'Avanzado' }
];

const EXPERIENCE_OPTIONS = [
  { value: 'principiante_total', label: 'Nunca he entrenado' },
  { value: 'corto', label: 'Menos de 6 meses' },
  { value: 'medio', label: 'Entre 6 meses y 1 año' },
  { value: 'largo', label: '1 a 3 años' },
  { value: 'experto', label: 'Más de 3 años' }
];

const TIME_OPTIONS = [
  { value: '30', label: '30 minutos' },
  { value: '45', label: '45 minutos' },
  { value: '60', label: '1 hora' },
  { value: '90', label: '1 hora 30 minutos o más' }
];

const HOME_EQUIPMENT = [
  { value: 'solo_corporal', label: 'Solo peso corporal' },
  { value: 'mancuernas', label: '+ Mancuernas' },
  { value: 'bandas', label: '+ Bandas elásticas' },
  { value: 'completo', label: 'Equipo completo (barra/discos)' }
];

const INJURED_ZONES = [
  { value: 'rodillas', label: 'Rodillas' },
  { value: 'espalda_baja', label: 'Espalda baja' },
  { value: 'hombros', label: 'Hombros' },
  { value: 'cadera', label: 'Cadera' }
];

const WorkoutForm = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('session_token') || localStorage.getItem('token');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasNutritionForm, setHasNutritionForm] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Analizando tu perfil...');
  const [allExercises, setAllExercises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [form, setForm] = useState({
    num_days: 4,
    level: 'principiante',
    location: 'casa',
    time_per_session: '60',
    experience: 'principiante_total',
    age: '',
    home_equipment: 'mancuernas',
    injured_zones: [],
    blocked_exercises: []
  });

  useEffect(() => {
    const init = async () => {
      try {
        const formRes = await axios.get(`${BACKEND_URL}/api/workout-form`, { headers: { Authorization: `Bearer ${token}` } });
        setHasNutritionForm(formRes.data.has_nutrition_form);
        if (formRes.data.form) {
          setForm(prev => ({
            ...prev,
            num_days: formRes.data.form.num_days || 4,
            level: formRes.data.form.level || 'principiante',
            location: formRes.data.form.location || 'casa',
            time_per_session: formRes.data.form.time_per_session || '60',
            experience: formRes.data.form.experience || 'principiante_total',
            age: formRes.data.form.age || '',
            home_equipment: formRes.data.form.home_equipment || 'mancuernas',
            injured_zones: formRes.data.form.injured_zones || [],
            blocked_exercises: formRes.data.form.blocked_exercises || []
          }));
        }
        if (formRes.data.has_nutrition_form) {
          const exRes = await axios.get(`${BACKEND_URL}/api/library-exercises`, { headers: { Authorization: `Bearer ${token}` } });
          setAllExercises(exRes.data.items || []);
        }
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [token]);

  const toggleBlocked = (exId) => {
    setForm(prev => ({
      ...prev,
      blocked_exercises: prev.blocked_exercises.includes(exId)
        ? prev.blocked_exercises.filter(id => id !== exId)
        : [...prev.blocked_exercises, exId]
    }));
  };

  const toggleZone = (zone) => {
    setForm(prev => ({
      ...prev,
      injured_zones: prev.injured_zones.includes(zone)
        ? prev.injured_zones.filter(z => z !== zone)
        : [...prev.injured_zones, zone]
    }));
  };

  const handleSubmit = async () => {
    setGenerating(true);
    const messages = [
      'Analizando tu perfil...',
      'Seleccionando los mejores ejercicios para ti...',
      'Organizando los días de entrenamiento...',
      'Ajustando tu cardio recomendado...',
      'Dándole los últimos toques...',
      'Casi listo, un momento más...'
    ];
    let msgIdx = 0;
    setLoadingMessage(messages[0]);
    const msgInterval = setInterval(() => {
      msgIdx = (msgIdx + 1) % messages.length;
      setLoadingMessage(messages[msgIdx]);
    }, 6000);

    try {
      const payload = { ...form };
      if (payload.age) payload.age = String(payload.age);
      // No mandar home_equipment si no es casa
      if (payload.location !== 'casa') delete payload.home_equipment;
      await axios.post(`${BACKEND_URL}/api/workout-form`, payload, { headers: { Authorization: `Bearer ${token}` } });
      await axios.post(`${BACKEND_URL}/api/workout-routine/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tu rutina está lista');
      navigate('/training-plan');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear tu rutina');
    } finally {
      clearInterval(msgInterval);
      setGenerating(false);
    }
  };

  const filteredExercises = allExercises.filter(ex =>
    !searchTerm || ex.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
      </div>
    );
  }

  // Bloqueo si no ha llenado nutrición
  if (!hasNutritionForm) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-zinc-900/60 border border-pink-500/30 rounded-3xl p-8 md:p-10 text-center" data-testid="needs-nutrition-form">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center">
            <Apple className="w-8 h-8 text-pink-400" />
          </div>
          <h2 className="font-barlow text-2xl md:text-3xl font-black text-white uppercase mb-3">Bienvenida 💕</h2>
          <p className="text-zinc-300 text-base mb-2">Como es tu primera vez, por favor llena el <span className="text-pink-400 font-bold">formulario de nutrición primero</span></p>
          <p className="text-zinc-500 text-sm mb-8">Algunas preguntas de tu entrenamiento dependen de tu perfil nutricional. Luego puedes continuar por aquí.</p>
          <Button onClick={() => navigate('/nutrition-form-premium')} className="bg-pink-600 hover:bg-pink-500 rounded-full px-8 py-6 font-bold uppercase tracking-wider w-full" data-testid="go-to-nutrition-btn">
            <Apple className="w-4 h-4 mr-2" /> Llenar formulario de nutrición
          </Button>
          <button onClick={() => navigate('/dashboard')} className="mt-4 text-zinc-500 hover:text-zinc-300 text-xs underline underline-offset-4">
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // Calcular total de pasos dinámicamente (home equipment solo si casa)
  const hasHomeStep = form.location === 'casa';
  const totalSteps = hasHomeStep ? 8 : 7;

  // Mapeo: paso → contenido
  // 1: días | 2: nivel | 3: lugar | 4: equipo en casa (condicional) | siguientes: experiencia, edad, tiempo, lesiones, bloqueados
  const stepIsHomeEquipment = step === 4 && hasHomeStep;
  // Si no es paso de equipo, ajustar offset
  const logicalStep = (() => {
    if (step <= 3) return step;
    if (hasHomeStep) {
      if (step === 4) return 'home_equipment';
      return step - 1; // step 5→4 (exp), 6→5 (edad), 7→6 (tiempo), 8→7 (lesion), 9→8 (bloq)
    }
    return step;
  })();
  // Total pasos lógicos: 8 (días, nivel, lugar, exp, edad, tiempo, lesiones, bloqueados) + opcional equipo
  // Recalcular total real = 8 (sin equipo) o 9 (con equipo)
  const realTotal = hasHomeStep ? 9 : 8;
  const isLastStep = step === realTotal;

  return (
    <div className="min-h-screen bg-[#09090b] py-12 px-4">
      {generating && (
        <div className="fixed inset-0 z-50 bg-[#09090b]/95 backdrop-blur-sm flex items-center justify-center p-6" data-testid="generating-overlay">
          <div className="max-w-md w-full text-center">
            <div className="relative w-32 h-32 mx-auto mb-8">
              <div className="absolute inset-0 rounded-full border-4 border-pink-500/20"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-pink-500 animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-pink-500/15 flex items-center justify-center">
                  <Dumbbell className="w-8 h-8 text-pink-400 animate-pulse" />
                </div>
              </div>
            </div>
            <h2 className="font-barlow text-2xl md:text-3xl font-black text-white uppercase mb-3">
              Preparando tu <span className="text-pink-500">plan</span>
            </h2>
            <p className="text-zinc-300 text-base min-h-[24px] transition-all" key={loadingMessage}>{loadingMessage}</p>
            <p className="text-zinc-500 text-xs mt-4">Esto puede tomar unos segundos. No cierres esta ventana.</p>
            <div className="flex items-center justify-center gap-1.5 mt-6">
              <span className="w-2 h-2 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-2 h-2 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-2 h-2 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-1.5 mb-2">
            {Array.from({ length: realTotal }, (_, i) => i + 1).map(s => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? 'bg-pink-500' : 'bg-zinc-800'}`} />
            ))}
          </div>
          <p className="text-zinc-500 text-xs">Paso {step} de {realTotal}</p>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8 md:p-10" data-testid="workout-form-card">
          {/* Step 1: Days */}
          {step === 1 && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Cuántos días puedes entrenar?</h2>
              <p className="text-zinc-400 mb-8 text-sm">Elige la cantidad de días por semana que tienes disponibles.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[3, 4, 5, 6].map(d => (
                  <button key={d} onClick={() => setForm({ ...form, num_days: d })} className={`p-6 rounded-2xl border-2 transition-all ${form.num_days === d ? 'border-pink-500 bg-pink-500/10 text-white' : 'border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-700'}`} data-testid={`days-${d}`}>
                    <div className="text-4xl font-black font-barlow">{d}</div>
                    <div className="text-xs uppercase mt-1 tracking-wider">días</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 2: Level */}
          {step === 2 && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Cuál es tu nivel?</h2>
              <p className="text-zinc-400 mb-8 text-sm">Sé honesta. Esto nos ayuda a ajustar la intensidad.</p>
              <div className="space-y-3">
                {LEVELS.map(l => (
                  <button key={l.value} onClick={() => setForm({ ...form, level: l.value })} className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${form.level === l.value ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`} data-testid={`level-${l.value}`}>
                    <div className="flex items-center justify-between">
                      <span className={`font-bold text-lg ${form.level === l.value ? 'text-white' : 'text-zinc-300'}`}>{l.label}</span>
                      {form.level === l.value && <Check className="w-5 h-5 text-pink-500" />}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 3: Location */}
          {step === 3 && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Dónde entrenas?</h2>
              <p className="text-zinc-400 mb-8 text-sm">Adaptamos los ejercicios al lugar.</p>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setForm({ ...form, location: 'casa' })} className={`p-8 rounded-2xl border-2 transition-all ${form.location === 'casa' ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`} data-testid="location-casa">
                  <Home className={`w-12 h-12 mx-auto mb-3 ${form.location === 'casa' ? 'text-pink-500' : 'text-zinc-500'}`} />
                  <div className={`font-bold text-lg ${form.location === 'casa' ? 'text-white' : 'text-zinc-300'}`}>Casa</div>
                </button>
                <button onClick={() => setForm({ ...form, location: 'gimnasio' })} className={`p-8 rounded-2xl border-2 transition-all ${form.location === 'gimnasio' ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`} data-testid="location-gimnasio">
                  <Building2 className={`w-12 h-12 mx-auto mb-3 ${form.location === 'gimnasio' ? 'text-pink-500' : 'text-zinc-500'}`} />
                  <div className={`font-bold text-lg ${form.location === 'gimnasio' ? 'text-white' : 'text-zinc-300'}`}>Gimnasio</div>
                </button>
              </div>
            </>
          )}

          {/* Step 4: Home equipment (solo si casa) */}
          {stepIsHomeEquipment && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Qué equipo tienes en casa?</h2>
              <p className="text-zinc-400 mb-8 text-sm">Para escogerte ejercicios que realmente puedas hacer.</p>
              <div className="space-y-3">
                {HOME_EQUIPMENT.map(opt => (
                  <button key={opt.value} onClick={() => setForm({ ...form, home_equipment: opt.value })} className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${form.home_equipment === opt.value ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`} data-testid={`equipment-${opt.value}`}>
                    <div className="flex items-center justify-between">
                      <span className={`font-bold text-base ${form.home_equipment === opt.value ? 'text-white' : 'text-zinc-300'}`}>{opt.label}</span>
                      {form.home_equipment === opt.value && <Check className="w-5 h-5 text-pink-500" />}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step: Experience */}
          {logicalStep === 4 && !stepIsHomeEquipment && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Hace cuánto entrenas?</h2>
              <p className="text-zinc-400 mb-8 text-sm">Tu experiencia real nos ayuda a calibrar mejor la rutina.</p>
              <div className="space-y-3">
                {EXPERIENCE_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => setForm({ ...form, experience: opt.value })} className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${form.experience === opt.value ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`} data-testid={`exp-${opt.value}`}>
                    <div className="flex items-center justify-between">
                      <span className={`font-bold text-base ${form.experience === opt.value ? 'text-white' : 'text-zinc-300'}`}>{opt.label}</span>
                      {form.experience === opt.value && <Check className="w-5 h-5 text-pink-500" />}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step: Age */}
          {logicalStep === 5 && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Cuál es tu edad?</h2>
              <p className="text-zinc-400 mb-8 text-sm">Para ajustar el volumen y la intensidad de forma segura.</p>
              <input
                type="number"
                value={form.age}
                onChange={(e) => setForm({ ...form, age: e.target.value })}
                placeholder="Ej. 28"
                min="14"
                max="80"
                className="w-full px-6 py-5 bg-zinc-800 border border-zinc-700 rounded-2xl text-white text-2xl font-bold focus:border-pink-500 outline-none text-center"
                data-testid="age-input"
              />
              <p className="text-zinc-500 text-xs mt-3 text-center">Este dato es completamente privado.</p>
            </>
          )}

          {/* Step: Time per session */}
          {logicalStep === 6 && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Cuánto tiempo tienes por sesión?</h2>
              <p className="text-zinc-400 mb-8 text-sm">Adaptamos la cantidad de ejercicios para que sea realista.</p>
              <div className="grid grid-cols-2 gap-3">
                {TIME_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => setForm({ ...form, time_per_session: opt.value })} className={`p-5 rounded-2xl border-2 transition-all ${form.time_per_session === opt.value ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`} data-testid={`time-${opt.value}`}>
                    <span className={`font-bold text-base ${form.time_per_session === opt.value ? 'text-white' : 'text-zinc-300'}`}>{opt.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step: Injured zones */}
          {logicalStep === 7 && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Tienes alguna lesión?</h2>
              <p className="text-zinc-400 mb-6 text-sm">Selecciona las zonas que debemos cuidar. Si no tienes ninguna, deja todo desmarcado.</p>
              <div className="space-y-3">
                {INJURED_ZONES.map(z => {
                  const sel = form.injured_zones.includes(z.value);
                  return (
                    <button key={z.value} onClick={() => toggleZone(z.value)} className={`w-full p-4 rounded-2xl border text-left transition-all flex items-center gap-3 ${sel ? 'border-red-500/60 bg-red-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`} data-testid={`zone-${z.value}`}>
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'border-red-500 bg-red-500' : 'border-zinc-600'}`}>
                        {sel && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className={`font-medium ${sel ? 'text-red-200' : 'text-white'}`}>{z.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Step: Blocked exercises */}
          {logicalStep === 8 && (
            <>
              <h2 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase mb-2">¿Hay ejercicios específicos que NO puedas hacer?</h2>
              <p className="text-zinc-400 mb-6 text-sm">Selecciona los ejercicios que debemos evitar. Puedes saltar este paso.</p>
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar ejercicio..." className="w-full mb-4 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-full text-white text-sm focus:border-pink-500 outline-none" />
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {filteredExercises.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-8">No hay ejercicios para mostrar.</p>
                ) : filteredExercises.map(ex => {
                  const selected = form.blocked_exercises.includes(ex.exercise_id);
                  return (
                    <button key={ex.exercise_id} onClick={() => toggleBlocked(ex.exercise_id)} className={`w-full p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${selected ? 'border-red-500/60 bg-red-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`} data-testid={`block-${ex.exercise_id}`}>
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-red-500 bg-red-500' : 'border-zinc-600'}`}>
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${selected ? 'text-red-200 line-through' : 'text-white'}`}>{ex.name}</div>
                        <div className="text-[11px] text-zinc-500 capitalize">{(ex.muscle_group || '').replace('_', ' ')}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {form.blocked_exercises.length > 0 && (
                <p className="text-xs text-red-400 mt-3">{form.blocked_exercises.length} ejercicio(s) bloqueados</p>
              )}
            </>
          )}

          <div className="flex items-center justify-between gap-3 mt-10 pt-6 border-t border-zinc-800">
            <Button variant="outline" onClick={() => step === 1 ? navigate('/dashboard') : setStep(step - 1)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full px-6" disabled={generating} data-testid="form-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Atrás
            </Button>

            {!isLastStep ? (
              <Button onClick={() => setStep(step + 1)} className="bg-pink-600 hover:bg-pink-500 rounded-full px-8 font-bold uppercase" data-testid="form-next-btn">
                Siguiente <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={generating} className="bg-pink-600 hover:bg-pink-500 rounded-full px-8 font-bold uppercase" data-testid="form-finish-btn">
                <Dumbbell className="w-4 h-4 mr-2" /> Crear mi plan
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutForm;
